/**
 * Governança das aprovações (ADR 0021): centros de custo, níveis de aprovação e responsáveis por
 * aprovação. Tudo é cadastrado nas telas; aqui ficam as validações e as regras de resolução.
 */
import { z } from 'zod';
import { COST_CENTER_LEVELS } from './cost-center.ts';
import { localizedText, uuid } from './schemas.ts';

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalDate = z.iso.date().nullable().optional();
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Ato formal de nomeação (especificação de alçadas, entrega 5): justificativa obrigatória,
 * referência da evidência e vigência opcional. Exigido quando a nomeação muda pelas telas.
 */
export const appointmentAct = z.object({
  justification: z.string().trim().min(10).max(1000),
  evidenceRef: z.string().trim().min(3).max(500).nullable().optional(),
  validUntil: optionalDate,
});
export type AppointmentAct = z.infer<typeof appointmentAct>;
export const APPOINTMENT_ROLES = [
  'cost_center_manager',
  'cost_center_substitute',
  'level_holder',
  'level_substitute',
] as const;
export type AppointmentRole = (typeof APPOINTMENT_ROLES)[number];

export const costCenterInput = z
  .object({
    legalEntityId: uuid,
    departmentId: uuid.nullable().optional(),
    parentId: uuid.nullable().optional(),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9._-]{0,39}$/, 'Use letras, números, ponto, hífen ou _ (ex.: CC-101)'),
    name: trimmed(200),
    /** Opcional: sem gestor, a aprovação da necessidade fica parada até o cadastro. */
    managerUserId: uuid.nullable().optional(),
    substituteUserId: uuid.nullable().optional(),
    /** Ato de nomeação do gestor e do substituto, quando mudam (entrega 5). */
    managerAppointment: appointmentAct.optional(),
    substituteAppointment: appointmentAct.optional(),
    isActive: z.boolean().default(true),
    validFrom: z.iso.date(),
    validUntil: optionalDate,
    /** Código do centro de custo no NetSuite (só a referência, sem integração). */
    netsuiteCode: optionalText(100),
    /**
     * Nível (ADR 0047): organiza e filtra, não dá alçada. Ausente (undefined) = mantém o que está
     * gravado.
     */
    structureLevel: z.enum(COST_CENTER_LEVELS).nullable().optional(),
    /**
     * Referência organizacional do Master (ADR 0099): só pode ser retirada (null), e então o Master
     * Responsável volta a ser o gestor do centro. Ausente (undefined) = mantém.
     */
    masterReferencePersonId: z.null().optional(),
  })
  .refine((v) => v.structureLevel !== 'master' || !v.parentId, {
    message: 'Um centro Master não tem centro superior',
    path: ['parentId'],
  })
  .refine((v) => !v.structureLevel || v.structureLevel === 'master' || !!v.parentId, {
    message: 'Um centro Gerencial ou Operacional precisa de um centro superior',
    path: ['parentId'],
  })
  .refine((v) => !v.substituteUserId || v.substituteUserId !== v.managerUserId, {
    message: 'O substituto precisa ser outra pessoa',
    path: ['substituteUserId'],
  })
  .refine((v) => !v.validUntil || v.validUntil >= v.validFrom, {
    message: 'O fim da vigência não pode ser antes do início',
    path: ['validUntil'],
  });
export type CostCenterInput = z.infer<typeof costCenterInput>;

/**
 * Centro novo e ativo (ADR 0047, adendo de 19/10/2026; ADR 0099): nível e gestor obrigatórios.
 * Os centros que já existem continuam podendo ser mantidos sem eles enquanto são regularizados.
 */
export function newCostCenterIssues(v: {
  isActive: boolean;
  structureLevel?: string | null;
  managerUserId?: string | null;
}): { path: string; message: string }[] {
  if (!v.isActive) return [];
  return [
    ...(v.structureLevel
      ? []
      : [
          {
            path: 'structureLevel',
            message: 'Centro novo ativo: escolha o nível (Master, Gerencial ou Operacional)',
          },
        ]),
    ...(v.managerUserId
      ? []
      : [
          {
            path: 'managerUserId',
            message: 'Centro novo ativo: escolha o gestor do centro de custo',
          },
        ]),
  ];
}

export const approvalLevelInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Use letras maiúsculas, números, hífen ou _'),
  name: localizedText,
  rank: z.number().int().min(1).max(99),
  isActive: z.boolean().default(true),
});
export type ApprovalLevelInput = z.infer<typeof approvalLevelInput>;

export const approvalResponsibleInput = z
  .object({
    costCenterId: uuid,
    levelId: uuid,
    holderUserId: uuid,
    substituteUserId: uuid.nullable().optional(),
    substituteFrom: optionalDate,
    substituteUntil: optionalDate,
    isActive: z.boolean().default(true),
    /** Ato de nomeação do titular e do substituto, quando mudam (entrega 5). */
    holderAppointment: appointmentAct.optional(),
    substituteAppointment: appointmentAct.optional(),
  })
  .refine((v) => v.substituteUserId !== v.holderUserId, {
    message: 'O substituto precisa ser outra pessoa',
    path: ['substituteUserId'],
  })
  .refine((v) => !!v.substituteUserId || (!v.substituteFrom && !v.substituteUntil), {
    message: 'O período de substituição precisa de um substituto',
    path: ['substituteUserId'],
  })
  .refine((v) => !v.substituteFrom || !v.substituteUntil || v.substituteUntil >= v.substituteFrom, {
    message: 'O fim da substituição não pode ser antes do início',
    path: ['substituteUntil'],
  });
export type ApprovalResponsibleInput = z.infer<typeof approvalResponsibleInput>;

/** Ausência de quem aprova (entrega 3): no período, a pessoa não aprova nem é avisada. */
export const approvalAbsenceInput = z
  .object({
    userId: uuid,
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    reason: z.string().trim().min(3).max(500).nullable().optional(),
    /** Quem decide no lugar no período (ADR 0074); obrigatório para quem aprova e registra a própria. */
    delegateUserId: uuid.nullable().optional(),
  })
  .refine((v) => v.endsOn >= v.startsOn, {
    message: 'O fim da ausência não pode ser antes do início',
    path: ['endsOn'],
  });
export type ApprovalAbsenceInput = z.infer<typeof approvalAbsenceInput>;

/** CEO que aprova as faixas que pedem o CEO (ADR 0080): sem empresa = grupo todo. */
export const approvalCeoInput = z
  .object({
    /** CEO (padrão) ou Head de BackOffice (ADR 0101). */
    office: z.enum(['ceo', 'ceo_substitute', 'backoffice_head']).default('ceo'),
    legalEntityId: uuid.nullable().optional(),
    userId: uuid,
    substituteUserId: uuid.nullable().optional(),
  })
  .refine((v) => !v.substituteUserId || v.substituteUserId !== v.userId, {
    message: 'O substituto precisa ser outra pessoa',
    path: ['substituteUserId'],
  });
export type ApprovalCeoInput = z.infer<typeof approvalCeoInput>;

/** Indicar ou trocar o substituto de uma ausência já registrada (ADR 0074). */
export const approvalAbsenceDelegateInput = z.object({
  delegateUserId: uuid,
  justification: z.string().trim().min(10).max(1000),
});
export type ApprovalAbsenceDelegateInput = z.infer<typeof approvalAbsenceDelegateInput>;

/** A pessoa está ausente na data (ausência não cancelada cobrindo o dia). */
export function isAbsent(
  absences: readonly { startsOn: string; endsOn: string; cancelledAt?: unknown }[],
  date: string,
): boolean {
  return absences.some((a) => !a.cancelledAt && a.startsOn <= date && date <= a.endsOn);
}

// ----- Resolução: quem responde por um nível num centro de custo -----

export interface CostCenterNode {
  id: string;
  parentId: string | null;
}

export interface ResponsibleLike {
  costCenterId: string;
  levelId: string;
  holderUserId: string;
  substituteUserId: string | null;
  substituteFrom: string | null;
  substituteUntil: string | null;
  isActive: boolean;
}

/** O substituto está autorizado na data? (sem período informado, está sempre autorizado). */
export function substituteActive(r: ResponsibleLike, date: string): boolean {
  if (!r.substituteUserId) return false;
  if (r.substituteFrom && date < r.substituteFrom) return false;
  if (r.substituteUntil && date > r.substituteUntil) return false;
  return true;
}

/**
 * Quem pode aprovar pelo nível no centro de custo, na data: o titular e, no período de
 * substituição, o substituto. Sem responsável ativo no centro de custo, sobe para o superior.
 * Devolve também em qual centro de custo o responsável foi encontrado.
 */
export function resolveResponsible(
  costCenterId: string,
  levelId: string,
  date: string,
  centers: readonly CostCenterNode[],
  responsibles: readonly ResponsibleLike[],
): { userIds: string[]; costCenterId: string | null } {
  const byId = new Map(centers.map((c) => [c.id, c]));
  const seen = new Set<string>();
  let current: string | null = costCenterId;
  while (current && !seen.has(current)) {
    seen.add(current);
    const found = responsibles.find(
      (r) => r.isActive && r.costCenterId === current && r.levelId === levelId,
    );
    if (found) {
      const ids = [found.holderUserId];
      if (substituteActive(found, date)) ids.push(found.substituteUserId!);
      return { userIds: ids, costCenterId: current };
    }
    current = byId.get(current)?.parentId ?? null;
  }
  return { userIds: [], costCenterId: null };
}
